import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {BehaviorSubject, Observable} from 'rxjs';
import {catchError, map, retry} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {CryptoService} from './crypto.service';
import {Login} from '../models/login';
import {HttpAbstract} from '../abstract/http.abstract';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService extends HttpAbstract {

  private currentUserSubject: BehaviorSubject<Login>;
  private currentUser: Observable<Login>;

  constructor(private cryptoService: CryptoService,
              private httpClient: HttpClient,
              private router: Router) {
    super();
    this.currentUserSubject = new BehaviorSubject<Login>(JSON.parse(localStorage.getItem('currentUser')));
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): Login {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string): Observable<Login> {
    // encrypt password
    password = this.cryptoService.encrypt(password);

    let headers = new HttpHeaders();
    headers = headers.set('http-x-access-key', environment.key.http_access)
      .set('Authorization', 'Basic ' + btoa(`${username}:${password}`));

    return this.httpClient
      .get<Login>(`${environment.url.api.security.login}`, {headers})
      .pipe(
        retry(1),
        catchError(this.handleError),
        map(data => {
          this.doLogin(data);
          return data;
        }));
  }

  logout() {
    this.currentUserSubject.next(null);
    localStorage.removeItem('currentUser');
    this.router.navigate(['/login']);
  }

  refreshToken(): Observable<Login> {
    return this.httpClient
      .get<Login>(`${environment.url.api.security.refresh_token}`)
      .pipe(
        retry(1),
        catchError(this.handleError),
        map(data => {
          this.doLogin(data);
          return data;
        }));
  }

  isLoggedIn() {
    if (this.currentUserValue) {
      // parse json object from base64 encoded token
      const token = JSON.parse(atob(this.currentUserValue.token.split('.')[1]));
      const currentTime = Math.round((new Date()).getTime() / 1000);
      if (token.exp > currentTime) {
        this.refreshToken();
        return true;
      } else {
        this.logout();
      }
    }
    return false;
  }

  private doLogin(data: Login) {
    if (data && data.token) {
      this.currentUserSubject.next(data);
      localStorage.setItem('currentUser', JSON.stringify(data));
    }
  }

}
